'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import AppIcon from '@/components/content/app/AppIcon'
import Badge from '@/components/ui/Badge'
import Link from '@/components/ui/Link'
import Pill from '@/components/ui/Pill'
import { createLogger } from '@/lib/logger'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { cn, getBrandColors, hexToRgba } from '@/lib/utils'
import { queryPayload } from '@/lib/payloadClient'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import { checkAppBranding } from '@/lib/formatting'
import type { App } from '@/payload-types'

const logger = createLogger('Ad')

type AdBlockProps = React.HTMLAttributes<HTMLDivElement> & {
  app: App | null
  className?: string
}

export default function Ad({ app, className }: AdBlockProps) {
  const [dealCount, setDealCount] = useState(0)

  useEffect(() => {
    async function fetchAndSetDeals(appId: App['id']) {
      try {
        const { docs: deals } = await queryPayload({
          collection: 'deal',
          select: {
            name: true,
            unitType: true,
            isReward: true,
            durationDays: true,
            quantity: true,
            order: true,
            featuredOrder: true,
            startDate: true,
            endDate: true,
          },
          where: { app: { equals: appId } },
          pagination: false,
          // Shape-suffixed cacheKey: Ad.tsx omits `product` from its select,
          // while AppOverview and CourseSidebar include it. Without the suffix
          // these three callers collide on the same key and whichever fires
          // first wins — later callers receive a response missing the field
          // they expected. See plan/KNOWN_ISSUES.md §2.
          cacheKey: `query:deal:app:${appId}:base`,
        })

        const dealsToDisplay = determineDealsToDisplay(deals)

        setDealCount(dealsToDisplay.length)
      } catch (error) {
        logger.error('Failed to fetch deals:', error)
      }
    }

    if (!app?.id) return

    void fetchAndSetDeals(app.id)
  }, [app])

  if (!app) return null

  const { primaryColor, secondaryColor, tertiaryColor, backgroundColor } = getBrandColors(app)
  const { appGlyph, appIcon, appHomepage } = checkAppBranding(app)
  const appHomepageUrl = appHomepage?.url

  const hasDeals = dealCount > 0
  const [link, linkTarget] = hasDeals
    ? [`/deals/${app.slug}?claim=true`, '_self']
    : [`/apps/${app.slug}`, '_self']

  return (
    <Link
      href={link}
      target={linkTarget}
      onClick={
        hasDeals ? () => trackFeaturedClick(FeaturedPlacement.BEST_MIDPAGE, app.name) : undefined
      }
      key={app.id}
      className={cn(
        'group relative mx-auto flex h-[440px] w-full max-w-2xl shrink-0 flex-col justify-end gap-5 rounded-2xl bg-gradient-to-t from-white to-zinc-50 p-6 after:absolute after:inset-0 after:rounded-2xl after:border after:border-white after:border-opacity-15 xs:min-w-[325px] xs:shrink md:h-[360px] md:min-w-0',
        className,
      )}
      style={{
        background: `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0)} 80%),
        radial-gradient(70% 100% at 100% 0%, ${hexToRgba(secondaryColor, 0.25)} 0%, ${hexToRgba(secondaryColor, 0)} 80%),
        radial-gradient(100% 100% at 50% 0%, ${hexToRgba(tertiaryColor, 0.25)} 0%, ${hexToRgba(tertiaryColor, 0)} 80%, rgba(0, 0, 0, 0) 100%),
        #000`,
      }}
    >
      <div className="flex w-full flex-col gap-6 md:flex-row md:items-center">
        <AppIcon
          icon={appIcon}
          glyph={appGlyph}
          name={app.name}
          background={backgroundColor}
          size={64}
          invert={true}
        />

        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2.5">
            <span className="line-clamp-1 w-fit text-20 font-semibold text-white">{app.name}</span>
            <Badge style="default">Featured</Badge>
          </div>
          <p className="line-clamp-1 text-13 text-white/90">{app.description} </p>
        </div>

        <div className="relative z-10 ml-auto flex gap-3 px-0.5">
          <Pill style="glass" theme="dark" className="ml-auto">
            Claim Deal
          </Pill>
        </div>
      </div>

      <div
        style={{
          WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0) 25%, rgba(0,0,0,1) 100%)',
          maskImage: 'linear-gradient(to top, rgba(0,0,0,0) 25%, rgba(0,0,0,1) 100%)',
        }}
        className="absolute left-0 top-0 z-0 h-full w-full overflow-hidden rounded-2xl"
      >
        <div className="backface-hidden absolute bottom-0 aspect-screen h-full translate-x-48 -rotate-[15deg] skew-x-12 skew-y-3 overflow-hidden rounded-2xl bg-white shadow-[0px_50px_100px_-25px_rgba(0,0,0,1)] transition-transform duration-1000 ease-in-out after:absolute after:inset-0 after:rounded-2xl after:border after:border-white after:border-opacity-15 group-hover:translate-x-44">
          {appHomepageUrl && (
            <Image
              src={appHomepageUrl}
              alt={`${app.name} homepage screenshot`}
              fill
              sizes="(min-width: 1024px) 50vw, 100vw"
              className="object-cover object-left-top"
            />
          )}
        </div>
      </div>
    </Link>
  )
}
